import * as React from 'react'
import {observer} from 'mobx-react'
import {FC} from 'react'
import {intersection} from 'lodash'
import cn from 'classnames'
import {
  isPostFinancePaymentMethod,
  PostFinancePaymentMethods,
} from '@raisenow/epik'
import {useStores} from '../../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../../lib/WidgetApi'
import {DynamicSlotName} from '../../../../../lib/DynamicSlotName'
import {DynamicSlot} from '../../../../dynamic/DynamicSlot'
import {PaymentForm} from '../../../../../lib/forms/PaymentForm'
import {SubscriptionRenewForm} from '../../../../../lib/forms/SubscriptionRenewForm'
import {PaymentMethodInfo} from '../PaymentMethodInfo'
import {InputRadioWrapper} from '../../../../wrappers/InputCheckableWrapper'

///////////////////////////////////////////////////////////////////////////////

export type PostFinanceFormProps = {
  form: PaymentForm | SubscriptionRenewForm
}

///////////////////////////////////////////////////////////////////////////////

export const PostFinanceForm: FC<PostFinanceFormProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {form} = props
  const paymentMethods = form.getPossiblePaymentMethods()
  const availablePostFinancePaymentMethods = intersection(
    paymentMethods,
    PostFinancePaymentMethods,
  )
  const selectedPaymentmethod = form.getPaymentMethod()

  if (availablePostFinancePaymentMethods.length === 0) {
    return null
  }
  if (!isPostFinancePaymentMethod(selectedPaymentmethod)) {
    return null
  }

  const paymentMethodInfoTextHTML = api.trans(
    `payment_method_info.${selectedPaymentmethod}.text_html`,
    {defaultValue: null},
  )

  if (availablePostFinancePaymentMethods.length === 1) {
    return <PaymentMethodInfo paymentMethod={selectedPaymentmethod} />
  }

  if (availablePostFinancePaymentMethods.length > 1) {
    return (
      <div className={cn('payment-method-form', selectedPaymentmethod)}>
        <DynamicSlot
          name={DynamicSlotName.POST_FINANCE_FORM_START}
          form={form}
        />

        <div className="main">
          <div className="list-items">
            {availablePostFinancePaymentMethods.map((paymentMethod, key) => {
              const isChecked = paymentMethod === selectedPaymentmethod
              const setPaymentMethod = () =>
                form.setPaymentMethod(paymentMethod)

              return (
                <label
                  key={key}
                  className={cn('label-radio-block', 'list-item', {
                    selected: isChecked,
                  })}
                >
                  <div className="label-radio-block-inner">
                    <div
                      className={cn(
                        'image',
                        'payment-method-bg-image-' + paymentMethod,
                      )}
                    />
                    <InputRadioWrapper>
                      <input
                        type="radio"
                        className="input-radio"
                        name="post_finance"
                        value={paymentMethod}
                        onChange={setPaymentMethod}
                        checked={isChecked}
                      />
                    </InputRadioWrapper>
                    <span className="title">
                      {api.trans(`payment_methods.${paymentMethod}`)}
                    </span>
                  </div>
                </label>
              )
            })}
          </div>

          {paymentMethodInfoTextHTML && (
            <div
              className="text"
              aria-live="polite"
              dangerouslySetInnerHTML={{__html: paymentMethodInfoTextHTML}}
            />
          )}
        </div>

        <DynamicSlot name={DynamicSlotName.POST_FINANCE_FORM_END} form={form} />
      </div>
    )
  }

  return null
})
